import React, { useCallback, useEffect, useState } from "react";
import { FileText, Loader2, Upload, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import BlueprintWalk from "./BlueprintWalk";
import PhotoTakeoffEditor from "./PhotoTakeoffEditor";

// SEND-301 slice 1 (Howard ruled 2026-08-14) — DRAW ON BLUEPRINTS: THE DOOR
// AND THE PAGE LIST. "Upload a PDF, or use the plan already on the job. Pull
// elevation sheets and floor-plan sheets BY THE TITLE ON THE PAGE. Skip the
// rest. BEFORE THE WALK OPENS, show what was kept and what was skipped. He can
// move a page between elevation, floor plan, and skip. NOTHING IS DRAWN UNTIL
// HE ACCEPTS THAT LIST."
//
// This door reads no drawing, measures nothing and writes NOT ONE LINE on the
// estimate. It shows the title each page carries, the bucket that title names,
// a thumbnail of the sheet, and his own moves on top. The walk (slices 2-5)
// opens only once he accepts.
const BUCKETS = [
  { key: "elevation", label: "Elevation" },
  { key: "floor_plan", label: "Floor plan" },
  { key: "skip", label: "Skip" },
];

const UPLOADS = `${process.env.REACT_APP_BACKEND_URL}/api/uploads`;

export default function BlueprintDrawButton({ est, onRederived = null }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [set, setSet] = useState(null);
  const [showList, setShowList] = useState(false);
  // SEND-301d (Howard ruled 2026-08-14) — **OPEN THE WALK IS THE FULL PHOTO
  // SCREEN.** It was mounted INSIDE this door's dialog, and a dialog is a
  // containing block, so the editor's own full-screen shell was squeezed into
  // a small window over the estimate: the sheet was cut off and the steps were
  // cut off. The editor is now mounted HERE, outside the dialog, exactly as
  // Measure from Photos mounts it — same component, same shell, same scale,
  // same steps, same rail. THERE IS NO SECOND SCREEN: this is that screen.
  const [walkKey, setWalkKey] = useState(null);
  const estId = est?.id;

  const load = useCallback(async () => {
    if (!estId) return;
    try {
      const r = await api.get(`/estimates/${estId}/blueprint-draw/sheets`);
      setSet(r.data);
    } catch (e) {
      if (e?.response?.status !== 404) {
        toast.error(e?.response?.data?.detail || "could not read the list");
      }
      setSet(null);
    }
  }, [estId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const build = async (fileOrNull) => {
    setBusy(fileOrNull ? "upload" : "job");
    try {
      const form = new FormData();
      if (fileOrNull) form.append("file", fileOrNull);
      else form.append("use_job_plan", "true");
      const r = await api.post(
        `/estimates/${estId}/blueprint-draw/sheets`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setSet(r.data);
      toast.success(
        `${r.data.pages_read} page(s) read · ${r.data.kept.length} kept`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "that PDF could not be read");
    } finally {
      setBusy("");
    }
  };

  const move = async (pageNum, bucket) => {
    setBusy(`p${pageNum}`);
    try {
      const r = await api.patch(
        `/estimates/${estId}/blueprint-draw/sheets/${pageNum}`, { bucket });
      setSet(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "that page did not move");
    } finally {
      setBusy("");
    }
  };

  const accept = async () => {
    setBusy("accept");
    try {
      const r = await api.post(`/estimates/${estId}/blueprint-draw/accept`);
      setSet(r.data);
      setShowList(false);
      toast.success("list accepted — the walk opens on the pages you kept");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "the list was not accepted");
    } finally {
      setBusy("");
    }
  };

  const pages = set?.pages || [];
  // the sheets he has already opened, so the rail can step between them the
  // way it steps between photos
  const openedPages = (set?.walk_pages || []).filter((p) => p.photo_key);

  return (
    <>
    {/* SEND-301f (Howard ruled 2026-08-14) — **THIS TILE LOOKS LIKE THE OTHER
        TWO.** "On the job page, Hover PDF and Measure from Photos are the same
        tile: a title, a purple button, and a line under it. Draw on Blueprints
        is a plain button with no line under it. Make that tile the same."
        So the button is THE HOUSE BUTTON — the same purple-on-surface markup,
        the same size, the same weight as `hover-import-btn` and
        `ai-measure-btn` — and ONE LINE sits under it saying what the door
        does. The tile has not moved, the walk has not changed, and Read
        Blueprints is not coming back. */}
    <div className="w-full flex flex-col gap-1.5">
    <Dialog open={open && !walkKey} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="w-full justify-center px-3 py-1.5 bg-[var(--surface)] text-[var(--ai)] border border-[var(--ai)] hover:bg-[var(--surface-muted)] text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50"
          data-testid="blueprint-draw-open"
          title="Draw on Blueprints — upload a plan or use the plan on the job, then the photo walk opens on the elevation pages"
        >
          <FileText className="w-3.5 h-3.5" />
          Draw on Blueprints
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto bg-[var(--surface,#ffffff)] text-[var(--ink,#09090B)]"
        data-testid="blueprint-draw-dialog">
        <DialogHeader>
          <DialogTitle>Draw on Blueprints</DialogTitle>
          <DialogDescription data-testid="blueprint-draw-description">
            Sort the pages, then open the photo walk on a page — the same
            scale, the same steps, the same chips, the same Apply All. You tag
            the wall in the walk.
          </DialogDescription>
        </DialogHeader>

        <p className="text-xs text-[var(--ink-2)]">
          Pages are sorted by the title printed on them. Nothing is read off the
          drawing and nothing is drawn until you accept the list.
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex">
            <input type="file" accept="application/pdf" className="hidden"
              data-testid="blueprint-draw-file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) build(f);
              }} />
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase border border-[var(--border)] cursor-pointer">
              {busy === "upload" ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Upload className="w-3 h-3" />}
              Upload a PDF
            </span>
          </label>
          <Button size="sm" variant="outline" disabled={!!busy}
            onClick={() => build(null)}
            data-testid="blueprint-draw-use-job-plan">
            {busy === "job" ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
              : null}
            Use the plan on this job
          </Button>
        </div>

        {set ? (
          <div className="space-y-3" data-testid="blueprint-draw-sheet-list">
            <div className="text-[11px] uppercase font-bold tracking-wider text-[var(--ink-2)]"
              data-testid="blueprint-draw-kept-summary">
              {set.pdf_name} · {set.pages_read} page(s) ·{" "}
              {set.elevation_pages.length} elevation ·{" "}
              {set.floor_plan_pages.length} floor plan ·{" "}
              {set.skipped.length} skipped
            </div>
            {set.walk_open && !showList ? (
              <BlueprintWalk estId={estId} set={set} onSet={setSet}
                onOpenPage={(key) => setWalkKey(key)} />
            ) : pages.map((p) => (
              <div key={p.page_num}
                className="flex gap-3 border border-[var(--border)] p-2"
                data-testid={`blueprint-draw-page-${p.page_num}`}>
                {p.thumb ? (
                  <img src={`${UPLOADS}/${p.thumb}`} alt={`page ${p.page_num}`}
                    className="w-24 h-24 object-contain bg-white border border-[var(--border)]"
                    data-testid={`blueprint-draw-thumb-${p.page_num}`} />
                ) : (
                  <div className="w-24 h-24 grid place-items-center text-[9px] text-[var(--muted)] border border-[var(--border)]">
                    no image
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold">
                    Page {p.page_num} ·{" "}
                    <span data-testid={`blueprint-draw-title-${p.page_num}`}>
                      {p.title || "no title found on this page"}
                    </span>
                  </div>
                  <div className="text-[10px] text-[var(--ink-2)] mt-0.5"
                    data-testid={`blueprint-draw-why-${p.page_num}`}>
                    {p.why}
                  </div>
                  <div className="flex gap-1.5 mt-2">
                    {BUCKETS.map((b) => (
                      <button key={b.key} type="button" disabled={!!busy}
                        onClick={() => move(p.page_num, b.key)}
                        className={`px-2 py-1 text-[10px] font-bold uppercase border disabled:opacity-40 ${p.bucket === b.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
                        data-testid={`blueprint-draw-bucket-${p.page_num}-${b.key}`}>
                        {b.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between gap-2 pt-1">
              <span className="text-[10px] text-[var(--muted)]"
                data-testid="blueprint-draw-walk-state">
                {set.walk_open
                  ? `accepted ${set.accepted_at?.slice(0, 16)} — the walk opens on ${set.kept.length} page(s)`
                  : "nothing is drawn until you accept this list"}
              </span>
              <Button size="sm" disabled={!!busy || !set.kept.length}
                onClick={accept} data-testid="blueprint-draw-accept">
                {busy === "accept"
                  ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                  : <Check className="w-3 h-3 mr-1.5" />}
                Accept this list
              </Button>
              {set.walk_open ? (
                <Button size="sm" variant="outline"
                  onClick={() => setShowList(!showList)}
                  data-testid="blueprint-draw-toggle-list">
                  {showList ? "Back to the walk" : "Back to the page list"}
                </Button>
              ) : null}
            </div>
          </div>
        ) : (
          <p className="text-xs text-[var(--muted)]"
            data-testid="blueprint-draw-empty">
            No PDF chosen for this door yet.
          </p>
        )}
      </DialogContent>
    </Dialog>
    {/* SEND-301f — THE ONE LINE UNDER THE BUTTON, the same box the other two
        tiles carry under theirs. It says what the door does and nothing else:
        no count, no figure, no promise. */}
    <div className="border border-[var(--border)] bg-[var(--surface-muted)] px-2 py-1 text-[11px] leading-snug text-[var(--muted)]"
      data-testid="blueprint-draw-tile-line">
      Upload a plan or use the plan on the job, then the photo walk opens on
      the elevation pages.
    </div>
    </div>
    {/* SEND-301d — THE SAME FULL PHOTO SCREEN AS MEASURE FROM PHOTOS. Mounted
        at this component's root and NOT inside the dialog above: a dialog is a
        containing block, and inside one this editor's own full-screen shell
        was squeezed into a small window with the sheet and the steps cut off.
        THE BLUEPRINT PAGE IS THE PHOTO — same scale, same steps, same rail,
        and nothing is cut out of the sheet. */}
    {walkKey ? (
      <PhotoTakeoffEditor
        key={walkKey}
        est={{ id: estId }}
        photoKey={walkKey}
        photoUrl={`/api/uploads/${walkKey}`}
        noAi
        photos={openedPages.map((p) => ({
          photo_key: p.photo_key, url: `/api/uploads/${p.photo_key}`,
          from: [`page ${p.page_num} of the plan${p.title ? ` — ${p.title}` : ""}`],
          marks: 0, confirmed_marks: 0, has_scale: false, face: null,
          face_by_hand: false,
        }))}
        onSwitch={(key) => setWalkKey(key)}
        onWrote={onRederived || undefined}
        onClose={() => { setWalkKey(null); setOpen(true); load(); }}
      />
    ) : null}
    </>
  );
}
